import {
  ArrowLeft,
  Aperture,
  CircleHelp,
  FileText,
  Heart,
  Images,
  House,
  LogIn,
  LogOut,
  Mail,
  Languages,
  Plus,
  Search,
  Settings,
  ShieldCheck,
  UserRound,
  Camera,
} from 'lucide-react'
import { motion, LayoutGroup } from 'framer-motion'
import { renderToStaticMarkup } from 'react-dom/server'
import { iosSelectionSpring } from '../../lib/iosMotion'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom'
import { AuthModal } from '../../components/auth/AuthModal'
import { AccountProfileActions } from '../../components/auth/AccountProfileActions'
import { PaywallSheet } from '../../components/membership/PaywallSheet'
import { FloatIslandSheet } from '../../components/ui/FloatIslandSheet'
import { VoxnelaMark } from '../../components/ui/VoxnelaMark'
import { useAuth } from '../../contexts/AuthContext'
import { useVoxnela } from '../../contexts/VoxnelaContext'
import { useStudioWorkspace } from '../../studio/contexts/StudioWorkspaceContext'
import { StudioSheetContent } from '../../studio/components/StudioSheetContent'
import { TaskShowcaseModal } from '../../components/tasks/TaskShowcaseModal'
import type { VoxnelaTaskItem } from '../../data/voxnela56Tasks'
import type { PaidMembershipPlan } from '../../lib/billingCatalog'
import type { PaywallRequest } from '../../lib/paywall'
import {
  formatMembershipDate,
  membershipPeriodLabel,
  membershipPlanLabel,
} from '../../lib/membership'
import { userInitials } from '../../lib/userInitials'
import { languageNativeNames, useUiText, type UiMessageKey } from '../../i18n/ui'
import { supportedLocales } from '../../types/simulation'
import { haptic } from '../../lib/native'
import {
  AUTH_REQUIRED_EVENT,
  GENERATION_AUTH_REQUIRED_MESSAGE,
} from '../../lib/authRequired'
import { privacyDocument, SUPPORT_EMAIL, termsDocument, type LegalDocument } from '../../data/legal'
import { localizeLegalDocument } from '../../i18n/content'
import { paywallMessages } from '../../i18n/paywall'
import { supportMailto } from '../../i18n/information'
import { useNativeKeyboardResize } from '../hooks/useKeyboardInset'
import { ALL_APPLICATION_STYLES } from '../../data/applicationStyleCatalog'
import { normalizeStyleSearchText, searchAndRankStyles } from '../../data/styleSearch'
import { searchCopy } from '../../data/styleSearchI18n'
import { favoriteCategoryLabels, localizeTasks, taskCategoryLabels } from '../../i18n/catalog'
import { VideoOnboarding } from './VideoOnboarding'
import { VideoAppSheet } from './VideoAppSheet'
import { VoxnelaAlertModal } from '../../components/ui/VoxnelaAlertModal'
import { guestMessages } from '../../i18n/guest'
import { guestSessionsSupported } from '../../lib/guestSession'
import { aiDataConsentCopy, requestAiDataConsent } from '../../lib/aiDataConsent'
import {
  NativeFloatIsland,
  supportsNativeFloatIsland,
  type NativeFloatIslandAction,
} from '../native/nativeFloatIsland'

export interface VideoShellOutletContext {
  openAuth: () => void
  openPaywall: (message?: string) => void
}

type SettingsView = 'main' | 'profile' | 'membership' | 'language' | 'contact' | 'privacy' | 'terms'

const navItems = [
  { to: '/', labelKey: 'home', icon: House, end: true },
  { to: '/explore', labelKey: 'explore', icon: Aperture, end: false },
  { to: '/projects', labelKey: 'gallery', icon: Images, end: false },
] as const

export function VideoAppShell() {
  useNativeKeyboardResize()
  const { locale, text, setLocale } = useUiText()
  const { user, isGuest, ensureGuestSession, logout, loading: authLoading } = useAuth()
  const guestCopy = guestMessages[locale]
  const { credits, membership, membershipSyncPending, membershipLoading, membershipKnown = true, workspaceError, refreshWorkspace } = useVoxnela()
  const membershipPending = membershipLoading
  const creditError = membershipSyncPending || workspaceError
  const planLabel = !membershipKnown && workspaceError ? paywallMessages[locale].membershipUnavailable : membershipPlanLabel(membership.plan, locale)
  const creditsLabel = creditError ? paywallMessages[locale].membershipSyncFailed : text('imageRights', { count: credits })
  const {
    openStudioWithPreset,
    sheetOpen,
    setSheetOpen,
    setActiveStylePreset,
    setSelectedSubcategory,
    setPresetPrompt,
    clearActiveProject,
  } = useStudioWorkspace()
  const navigate = useNavigate()
  const location = useLocation()
  const [authOpen, setAuthOpen] = useState(false)
  const [authMessage, setAuthMessage] = useState('')
  const [guestSessionFailed, setGuestSessionFailed] = useState(false)
  const [guestSessionRetrying, setGuestSessionRetrying] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [settingsView, setSettingsView] = useState<SettingsView>('main')
  const [paywallOpen, setPaywallOpen] = useState(false)
  const [paywallInitialPlan, setPaywallInitialPlan] = useState<PaidMembershipPlan | undefined>()
  const [paywallMessage, setPaywallMessage] = useState<string>()
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchPreviewTask, setSearchPreviewTask] = useState<VoxnelaTaskItem | null>(null)
  const [onboardingReplay, setOnboardingReplay] = useState(0)
  const [onboardingVisible, setOnboardingVisible] = useState(false)
  const [onboardingPaywallPending, setOnboardingPaywallPending] = useState(false)
  const [externalSheetOpen, setExternalSheetOpen] = useState(false)
  const [nativeFloatIslandReady, setNativeFloatIslandReady] = useState(false)
  const nativeFloatIslandActionRef = useRef<(action: NativeFloatIslandAction) => void>(() => {})
  const searchInputRef = useRef<HTMLInputElement>(null)
  const searchCaretPendingRef = useRef(false)
  const wordmarkRef = useRef<HTMLAnchorElement>(null)
  const searchButtonRef = useRef<HTMLButtonElement>(null)
  const nativeFloatIslandSupported = supportsNativeFloatIsland()
  const nativeIconImages = useMemo(
    () => nativeFloatIslandSupported ? createNativeFloatIslandIcons() : Promise.resolve({}),
    [nativeFloatIslandSupported],
  )
  const cachedIconsRef = useRef<Record<string, string>>({})
  useEffect(() => {
    if (!onboardingPaywallPending || onboardingVisible || authLoading || (user && (membershipLoading || !membershipKnown))) return
    let active = true
    queueMicrotask(() => {
      if (!active) return
      setOnboardingPaywallPending(false)
      if (user && membership.plan === 'studio') return
      setSettingsOpen(false)
      setSearchOpen(false)
      setSheetOpen(false)
      setPaywallMessage(undefined)
      setPaywallInitialPlan(user && (membership.plan === 'pro' || membership.plan === 'plus') ? 'studio' : 'pro')
      setPaywallOpen(true)
    })
    return () => { active = false }
  }, [authLoading, membership.plan, membershipKnown, membershipLoading, onboardingPaywallPending, onboardingVisible, setSheetOpen, user])
  useEffect(() => {
    void nativeIconImages.then((icons) => {
      cachedIconsRef.current = icons
    })
  }, [nativeIconImages])

  // Some sheets (gallery image detail, style previews) are opened by page
  // components outside this shell. Keep the native island in sync with every
  // sheet root, including those the shell does not own directly.
  useEffect(() => {
    const syncSheetVisibility = () => {
      const hasOpenSheet = Boolean(document.querySelector(
        '.float-island-sheet-root, .visual-style-modal-root, .studio-creation-sheet, .video-settings-sheet, .modal-backdrop, .voxnela-alert-backdrop, [role="dialog"], [data-sheet-open="true"]',
      ))
      setExternalSheetOpen(hasOpenSheet)
      if (hasOpenSheet) {
        document.documentElement.classList.add('app-sheet-open')
      } else {
        document.documentElement.classList.remove('app-sheet-open')
      }
    }
    syncSheetVisibility()
    const observer = new MutationObserver(syncSheetVisibility)
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'data-sheet-open'] })
    return () => {
      observer.disconnect()
      document.documentElement.classList.remove('app-sheet-open')
    }
  }, [])

  useEffect(() => {
    const handleAuthRequired = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: string }>).detail
      // In the app no feature needs an account: start the guest session instead.
      if (guestSessionsSupported()) {
        void ensureGuestSession().then((guest) => {
          if (!guest) setGuestSessionFailed(true)
        })
        return
      }
      setSettingsOpen(false)
      setSearchOpen(false)
      setSheetOpen(false)
      setAuthMessage(detail?.message || GENERATION_AUTH_REQUIRED_MESSAGE)
      setAuthOpen(true)
    }
    window.addEventListener(AUTH_REQUIRED_EVENT, handleAuthRequired)
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, handleAuthRequired)
  }, [ensureGuestSession, setSheetOpen])

  const retryGuestSession = async () => {
    setGuestSessionRetrying(true)
    const guest = await ensureGuestSession()
    setGuestSessionRetrying(false)
    if (guest) setGuestSessionFailed(false)
  }

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [location.pathname])

  useEffect(() => {
    const handleOpenPaywall = (event: Event) => {
      const request = (event as CustomEvent<PaywallRequest>).detail ?? {}
      setSettingsOpen(false)
      setSearchOpen(false)
      setSheetOpen(false)
      setPaywallInitialPlan(request.initialPlan)
      setPaywallMessage(request.message)
      setPaywallOpen(true)
    }
    window.addEventListener('voxnela:open-paywall', handleOpenPaywall)
    return () => {
      window.removeEventListener('voxnela:open-paywall', handleOpenPaywall)
    }
  }, [setSheetOpen])

  const outletContext = useMemo<VideoShellOutletContext>(() => ({
    openAuth: () => setAuthOpen(true),
    openPaywall: (message) => {
      setPaywallMessage(message)
      setPaywallOpen(true)
    },
  }), [])

  const openSettings = useCallback(() => {
    setSheetOpen(false)
    setSearchOpen(false)
    setSettingsView('main')
    setSettingsOpen(true)
  }, [setSheetOpen])

  const openSearch = useCallback(() => {
    setSheetOpen(false)
    setSettingsOpen(false)
    searchCaretPendingRef.current = true
    setSearchOpen(true)
  }, [setSheetOpen])

  const openFavorites = useCallback(() => {
    void haptic('selection')
    setSheetOpen(false)
    setSettingsOpen(false)
    setSearchOpen(false)
    navigate('/explore?category=favorites', { state: { favoritesNavigationId: Date.now() } })
  }, [navigate, setSheetOpen])

  const newProject = useCallback(() => {
    void haptic('selection')
    setSettingsOpen(false)
    setSearchOpen(false)
    clearActiveProject()
    setActiveStylePreset(null)
    setSelectedSubcategory(null)
    setPresetPrompt(null)
    setSheetOpen(true)
  }, [clearActiveProject, setActiveStylePreset, setPresetPrompt, setSelectedSubcategory, setSheetOpen])

  const showBack = location.pathname !== '/'


  const handleBack = useCallback(() => {
    if (location.pathname.startsWith('/project/')) {
      navigate('/projects')
    } else {
      navigate('/')
    }
  }, [location.pathname, navigate])
  const localizedSearchStyles = useMemo(() => localizeTasks(ALL_APPLICATION_STYLES, locale), [locale])
  const normalizedSearch = normalizeStyleSearchText(searchQuery)
  const styleResults = useMemo(() => normalizedSearch
    ? searchAndRankStyles(localizedSearchStyles, normalizedSearch).slice(0, 48)
    : [], [localizedSearchStyles, normalizedSearch])
  const searchPreviewStyles = useMemo(() => styleResults.map(({ item }) => item), [styleResults])
  const previewSearchStyle = (task: VoxnelaTaskItem) => {
    void haptic('selection')
    searchInputRef.current?.blur()
    // Search hits always open the style preview. Explicitly dismiss any stale
    // studio/settings sheet so its production UI cannot win the sheet race.
    setSheetOpen(false)
    setSettingsOpen(false)
    setSearchOpen(false)
    setSearchPreviewTask(task)
  }

  const useSearchStyle = (task: VoxnelaTaskItem) => {
    const subcategoryId = task.recommendedStyleId || task.id
    // This is the only search-style path that opens the generation sheet.
    setSearchPreviewTask(null)
    setSearchOpen(false)
    setSettingsOpen(false)
    openStudioWithPreset(task.studioMode, subcategoryId, undefined, {
      id: task.id,
      title: task.title,
      imageUrl: task.sampleImageUrl,
      category: task.category,
      description: task.description,
      realPrompt: task.promptTemplate,
      suggestions: task.suggestions,
      mode: task.studioMode,
      subcategoryId,
      aspectRatio: task.aspectRatio,
    })
  }

  const nativeActiveItem: NativeFloatIslandAction = sheetOpen
    ? 'new'
    : settingsOpen
    ? 'settings'
    : location.pathname === '/new'
      ? 'new'
      : location.pathname === '/projects' || location.pathname === '/gallery' || location.pathname.startsWith('/project/')
        ? 'projects'
        : location.pathname.startsWith('/explore')
          ? 'explore'
          : 'home'
  const nativeLabels = useMemo(() => ({
    home: text('home'),
    explore: text('explore'),
    projects: text('gallery'),
    settings: text('settings'),
    new: text('createImage'),
    login: text('login'),
    navigation: `Voxnela · ${text('home')}`,
  }), [text])
  const nativeFloatIslandVisible = !onboardingVisible
    && !sheetOpen
    && !settingsOpen
    && !searchOpen
    && !paywallOpen
    && !authOpen
    && !searchPreviewTask
    && !externalSheetOpen

  useEffect(() => {
    nativeFloatIslandActionRef.current = (action) => {
      if (action === 'login') {
        setAuthOpen(true)
        return
      }
      if (action === 'new') {
        if (sheetOpen) setSheetOpen(false)
        else newProject()
        return
      }
      void haptic('selection')
      if (action === 'settings') {
        openSettings()
        return
      }
      setSettingsOpen(false)
      setSearchOpen(false)
      setSheetOpen(false)
      navigate(action === 'home' ? '/' : `/${action}`)
    }
  }, [navigate, newProject, openSettings, setSheetOpen, sheetOpen])

  useEffect(() => {
    if (!nativeFloatIslandSupported) return
    let disposed = false
    let listener: Awaited<ReturnType<typeof NativeFloatIsland.addListener>> | undefined

    void (async () => {
      try {
        listener = await NativeFloatIsland.addListener('action', ({ action }) => {
          nativeFloatIslandActionRef.current(action)
        })
        const icons = await nativeIconImages.catch(() => ({}))
        await NativeFloatIsland.show({
          active: nativeActiveItem,
          visible: nativeFloatIslandVisible,
          labels: nativeLabels,
          icons,
        })
        if (disposed) return
        document.documentElement.classList.add('native-float-island')
        document.documentElement.dataset.voxnelaNativeFloatIsland = 'true'
        setNativeFloatIslandReady(true)
      } catch {
        document.documentElement.classList.remove('native-float-island')
        delete document.documentElement.dataset.voxnelaNativeFloatIsland
      }
    })()

    return () => {
      disposed = true
      void listener?.remove()
      void NativeFloatIsland.hide().catch(() => undefined)
      document.documentElement.classList.remove('native-float-island')
      delete document.documentElement.dataset.voxnelaNativeFloatIsland
    }
    // The listener is installed once; current actions are routed through a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nativeFloatIslandSupported, nativeIconImages])

  useEffect(() => {
    if (!nativeFloatIslandReady) return
    const icons = cachedIconsRef.current
    void NativeFloatIsland.update({
      active: nativeActiveItem,
      visible: nativeFloatIslandVisible,
      labels: nativeLabels,
      icons,
    }).catch(() => undefined)
  }, [nativeActiveItem, nativeFloatIslandReady, nativeLabels, nativeFloatIslandVisible])

  useEffect(() => {
    if (!searchOpen) return
    let timeout = 0
    const focusAtStart = () => {
      const input = searchInputRef.current
      if (!input) return
      input.focus({ preventScroll: true })
      input.setSelectionRange(0, 0)
      searchCaretPendingRef.current = false
    }
    const frame = window.requestAnimationFrame(() => {
      focusAtStart()
      timeout = window.setTimeout(focusAtStart, 320)
    })
    return () => {
      window.cancelAnimationFrame(frame)
      window.clearTimeout(timeout)
    }
  }, [searchOpen])

  const closeFloatingSheets = () => {
    setSettingsOpen(false)
    setSearchOpen(false)
    setSheetOpen(false)
  }

  const floatingIsland = (
    <div className="video-floating-wrap fortale-floatisland-root" aria-label={text('home')}>
      <div className="floatisland-cluster">
        <LayoutGroup id="video-floating-island-selection">
          <nav className="video-floating-island floatisland-nav">
            {navItems.map(({ to, labelKey, icon: Icon, end }) => {
              const label = text(labelKey)
              const projectSectionActive = to === '/projects' && (location.pathname.startsWith('/project/') || location.pathname === '/gallery')
              const active = !settingsOpen && !sheetOpen && (location.pathname === to || projectSectionActive)
              return (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={closeFloatingSheets}
                  className={`floatisland-item${active ? ' active is-active' : ''}`}
                  aria-label={label}
                  aria-current={active ? 'page' : undefined}
                >
                  {active && (
                    <motion.span
                      className="floatisland-active-glass"
                      layoutId="video-floatisland-active-glass"
                      aria-hidden="true"
                      transition={iosSelectionSpring}
                    />
                  )}
                  {to === '/'
                    ? <span className="floatisland-home-mark" aria-hidden="true"><VoxnelaMark /></span>
                    : <Icon size={20} strokeWidth={active ? 2.6 : 2.35} aria-hidden="true" />}
                  {active && <span className="floatisland-label">{label}</span>}
                </NavLink>
              )
            })}
            <button
              type="button"
              className={`floatisland-item${settingsOpen ? ' active is-active' : ''}`}
              onClick={openSettings}
              aria-label={text('settings')}
              aria-expanded={settingsOpen}
            >
              {settingsOpen && (
                <motion.span
                  className="floatisland-active-glass"
                  layoutId="video-floatisland-active-glass"
                  aria-hidden="true"
                  transition={iosSelectionSpring}
                />
              )}
              <Settings size={20} strokeWidth={settingsOpen ? 2.6 : 2.35} aria-hidden="true" />
              {settingsOpen && <span className="floatisland-label">{text('settings')}</span>}
            </button>
          </nav>
        </LayoutGroup>
        <button
          className={`video-new-button floatisland-quick-add-trigger${sheetOpen ? ' active' : ''}`}
          type="button"
          onClick={newProject}
          aria-label={text('createImage')}
          title={text('createImage')}
        >
          <Plus size={24} strokeWidth={2.5} aria-hidden="true" />
          <span>{text('createImage')}</span>
        </button>
      </div>
    </div>
  )

  return (
    <div className="video-app">
      <div className="video-ocean-canvas" aria-hidden="true" />
      <div className="app-status-bar-layer" aria-hidden="true" />
      <div className="video-header-mist" aria-hidden="true" />
      <header className="video-topbar">
        <NavLink ref={wordmarkRef} className="video-wordmark" to="/" aria-label={`Voxnela ${text('home')}`}>
          <VoxnelaMark />
        </NavLink>
        <div className="video-header-actions">
          {showBack && (
            <button
              type="button"
              className="apple-tv-header-icon-btn"
              onClick={handleBack}
              aria-label={text('back')}
              title={text('back')}
            >
              <ArrowLeft size={16} strokeWidth={2.4} />
            </button>
          )}
          <button
            ref={searchButtonRef}
            type="button"
            className="apple-tv-header-icon-btn"
            onClick={openSearch}
            aria-label={text('search')}
            title={text('search')}
          >
            <Search size={16} strokeWidth={2.4} />
          </button>
          <button
            type="button"
            className="apple-tv-header-icon-btn"
            onClick={openFavorites}
            aria-label={favoriteCategoryLabels[locale]}
            title={favoriteCategoryLabels[locale]}
          >
            <Heart size={17} strokeWidth={2.4} aria-hidden="true" />
          </button>
        </div>
      </header>

      <main className="video-main">
        <Outlet context={outletContext} />
      </main>

      {!nativeFloatIslandSupported && (
        typeof document === 'undefined' ? floatingIsland : createPortal(floatingIsland, document.body)
      )}

      <VideoOnboarding
        key={onboardingReplay}
        forceOpen={onboardingReplay > 0}
        onVisibilityChange={setOnboardingVisible}
        onComplete={() => setOnboardingPaywallPending(true)}
      />

      <VoxnelaAlertModal
        open={guestSessionFailed}
        title="Voxnela"
        message={guestCopy.guestSessionFailed}
        primaryLabel={paywallMessages[locale].retry}
        primaryLoading={guestSessionRetrying}
        onPrimary={() => void retryGuestSession()}
        onClose={() => setGuestSessionFailed(false)}
      />
      <AuthModal
        open={authOpen}
        onClose={() => {
          setAuthOpen(false)
          setAuthMessage('')
        }}
        message={authMessage}
      />
      <PaywallSheet
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        initialPlan={paywallInitialPlan}
        message={paywallMessage}
        onOpenTerms={() => {
          setPaywallOpen(false)
          setSettingsView('terms')
          setSettingsOpen(true)
        }}
        onOpenPrivacy={() => {
          setPaywallOpen(false)
          setSettingsView('privacy')
          setSettingsOpen(true)
        }}
      />

      <VideoAppSheet
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onBack={settingsView === 'main' ? undefined : () => setSettingsView('main')}
        title={settingsTitle(settingsView, text)}
        subtitle={
          settingsView === 'main'
            ? text('preferencesSubtitle')
            : settingsView === 'profile'
              ? text('accountSubtitle')
              : undefined
        }
      >
        {settingsView === 'main' ? (
          <div className="video-settings-list">
            <button
              type="button"
              onClick={() => {
                if (user) {
                  setSettingsView('profile')
                } else {
                  setSettingsOpen(false)
                  setAuthOpen(true)
                }
              }}
            >
              <span className="video-settings-icon"><UserRound size={19} /></span>
              <span>
                <strong>{isGuest ? guestCopy.guestName : user?.displayName || user?.email || text('voxAccount')}</strong>
                <small>{user ? membershipPending ? paywallMessages[locale].membershipSyncPending : `${planLabel} · ${creditsLabel}` : text('accessEverywhere')}</small>
              </span>
              {user ? <span className="video-settings-value">{text('profile')}</span> : <LogIn size={18} />}
            </button>
            <button type="button" onClick={() => setSettingsView('membership')}>
              <span className="video-settings-icon"><CircleHelp size={19} /></span>
              <span><strong>{text('plans')}</strong><small>{text('plansSubtitle')}</small></span>
            </button>
            <button type="button" onClick={() => setSettingsView('language')}>
              <span className="video-settings-icon"><Languages size={19} /></span>
              <span><strong>{text('language')}</strong><small>{text('languageSubtitle')}</small></span>
              <span className="video-settings-value">{languageNativeNames[locale]}</span>
            </button>
            <button type="button" onClick={() => setSettingsView('contact')}>
              <span className="video-settings-icon"><Mail size={19} /></span>
              <span><strong>{text('contact')}</strong><small>{text('contactSubtitle')}</small></span>
            </button>
            <button type="button" onClick={() => { setSettingsOpen(false); setOnboardingReplay((current) => current + 1) }}>
              <span className="video-settings-icon"><Camera size={19} /></span>
              <span><strong>{text('discoverVoxnela')}</strong><small>{text('discoverVoxnelaSubtitle')}</small></span>
            </button>
            <button type="button" onClick={() => setSettingsView('privacy')}>
              <span className="video-settings-icon"><ShieldCheck size={19} /></span>
              <span><strong>{text('privacy')}</strong><small>{text('privacySubtitle')}</small></span>
            </button>
            {user && (
              <button type="button" onClick={() => { void requestAiDataConsent(user.uid, true).catch(() => undefined) }}>
                <span className="video-settings-icon"><ShieldCheck size={19} /></span>
                <span><strong>{aiDataConsentCopy(locale).title}</strong><small>{aiDataConsentCopy(locale).settings}</small></span>
              </button>
            )}
            <button type="button" onClick={() => setSettingsView('terms')}>
              <span className="video-settings-icon"><FileText size={19} /></span>
              <span><strong>{text('terms')}</strong><small>{text('termsSubtitle')}</small></span>
            </button>
            {user && !isGuest ? (
              <button className="video-settings-auth is-danger" type="button" onClick={() => void logout()}>
                <LogOut size={18} />
                <span>{text('logout')}</span>
              </button>
            ) : (
              <button className="video-settings-auth" type="button" onClick={() => { setSettingsOpen(false); setAuthOpen(true) }}>
                <LogIn size={18} />
                <span>{isGuest ? guestCopy.guestSignIn : text('login')}</span>
              </button>
            )}
          </div>
        ) : settingsView === 'profile' ? (
          <div className="video-profile-view">
            <div className="account-profile-summary">
              <div className="account-membership-copy">
                <div className="account-membership-heading">
                  <strong>{membershipPending ? paywallMessages[locale].membershipSyncPending : planLabel}</strong>
                  {membership.plan !== 'free' && (
                    <span>
                      {membershipPeriodLabel(membership.billingPeriod, locale) ?? text('monthly')}
                      {!membership.willRenew && ` · ${text('renewalOff')}`}
                    </span>
                  )}
                </div>
                <dl>
                  <div>
                    <dt>{text('availableCredits')}</dt>
                    <dd>{membershipPending ? paywallMessages[locale].membershipSyncPending : creditsLabel}</dd>
                  </div>
                  <div>
                    <dt>{membership.plan !== 'free' ? text('membershipStart') : text('registrationDate')}</dt>
                    <dd>{formatMembershipDate(membership.planStartedAtMs, locale)}</dd>
                  </div>
                  {membership.plan !== 'free' && (
                    <div>
                      <dt>{text('renewalDate')}</dt>
                      <dd>{formatMembershipDate(membership.renewalAtMs, locale)}</dd>
                    </div>
                  )}
                </dl>
              </div>
              <div className="account-identity">
                <span className="auth-avatar">
                  {isGuest ? guestCopy.guestName.slice(0, 1) : user ? userInitials(user.displayName, user.email) : 'TT'}
                </span>
                <strong>{isGuest ? guestCopy.guestName : user?.displayName ?? text('user')}</strong>
                <small>{isGuest ? guestCopy.guestStatus : user?.email}</small>
              </div>
            </div>
            {isGuest && (
              <div className="video-guest-account">
                <p className="video-muted-copy">{guestCopy.guestHint}</p>
                <button
                  className="video-primary-button"
                  type="button"
                  onClick={() => {
                    setSettingsOpen(false)
                    setAuthOpen(true)
                  }}
                >
                  {guestCopy.guestSignIn}
                </button>
              </div>
            )}
            <AccountProfileActions
              onSessionEnded={() => {
                setSettingsView('main')
              }}
            />
          </div>
        ) : settingsView === 'membership' ? (
          <MembershipSettings
            signedIn={Boolean(user)}
            membership={planLabel}
            credits={credits}
            pending={membershipPending}
            error={creditError}
            onRetry={() => void refreshWorkspace().catch(() => {})}
            onLogin={() => {
              setSettingsOpen(false)
              setAuthMessage(text('signInIntro'))
              setAuthOpen(true)
            }}
            onUpgrade={() => {
              setSettingsOpen(false)
              setPaywallInitialPlan(
                membership.plan === 'plus' || membership.plan === 'pro'
                  ? 'studio'
                  : 'pro',
              )
              setPaywallOpen(true)
            }}
          />
        ) : settingsView === 'language' ? (
          <div className="video-settings-list video-language-list" role="radiogroup" aria-label={text('language')}>
            {supportedLocales.map((language) => (
              <button
                key={language}
                type="button"
                role="radio"
                aria-checked={language === locale}
                className={language === locale ? 'is-selected' : undefined}
                onClick={() => void setLocale(language)}
              >
                <span className="video-settings-icon"><Languages size={19} /></span>
                <span><strong>{languageNativeNames[language]}</strong><small>{language.toUpperCase()}</small></span>
                {language === locale && <span className="video-settings-value">✓</span>}
              </button>
            ))}
          </div>
        ) : settingsView === 'contact' ? (
          <InfoCopy>
            <p>{text('supportBody')}</p>
            <a className="video-primary-button" href={supportMailto(locale, SUPPORT_EMAIL)}>{SUPPORT_EMAIL}</a>
          </InfoCopy>
        ) : settingsView === 'privacy' ? (
          <LegalCopy document={localizeLegalDocument('privacy', locale, privacyDocument)} />
        ) : (
          <LegalCopy document={localizeLegalDocument('terms', locale, termsDocument)} />
        )}
      </VideoAppSheet>

      <FloatIslandSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title={text('studioTitle')}
        subtitle={text('studioSubtitle')}
        icon={<VoxnelaMark />}
        className="studio-creation-sheet"
      >
        <StudioSheetContent />
      </FloatIslandSheet>

      <FloatIslandSheet
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        title={searchCopy[locale].title}
        subtitle={searchCopy[locale].subtitle}
        className="video-search-sheet"
      >
        <div className="video-search-content">
          <label className="video-search-input">
            <Search size={17} />
            <input
              ref={searchInputRef}
              type="search"
              inputMode="search"
              enterKeyHint="search"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onFocus={(event) => {
                if (!searchCaretPendingRef.current) return
                const input = event.currentTarget
                window.requestAnimationFrame(() => {
                  input.setSelectionRange(0, 0)
                  searchCaretPendingRef.current = false
                })
              }}
              autoFocus
              placeholder={searchCopy[locale].placeholder}
            />
          </label>
          <div className="video-search-results">
            {styleResults.map(({ item }) => (
              <button
                type="button"
                key={item.id}
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  previewSearchStyle(item)
                }}
              >
                <span className="video-search-result-thumb"><img src={item.sampleImageUrl} alt="" loading="lazy" /></span>
                <span className="video-search-result-copy"><strong>{item.title}</strong><small>{searchCopy[locale].style} · {taskCategoryLabels[locale][item.category]}</small></span>
                <ArrowLeft size={16} className="video-search-result-arrow" />
              </button>
            ))}
            {styleResults.length === 0 && normalizedSearch && <p>{text('noSearchResults')}</p>}
          </div>
        </div>
      </FloatIslandSheet>

      <TaskShowcaseModal
        open={Boolean(searchPreviewTask)}
        task={searchPreviewTask}
        tasks={searchPreviewStyles}
        onClose={() => setSearchPreviewTask(null)}
        onUseTask={useSearchStyle}
      />
    </div>
  )
}

async function createNativeFloatIslandIcons(): Promise<Record<string, string>> {
  const entries = [
    ['explore', renderToStaticMarkup(<Aperture size={20} strokeWidth={2.35} color="#fff" />), 60],
    ['projects', renderToStaticMarkup(<Images size={20} strokeWidth={2.35} color="#fff" />), 60],
    ['settings', renderToStaticMarkup(<Settings size={20} strokeWidth={2.35} color="#fff" />), 60],
    ['new', renderToStaticMarkup(<Plus size={24} strokeWidth={2.5} color="#fff" />), 72],
  ] as const

  const images = await Promise.all(entries.map(async ([key, markup, size]) => {
    const svg = markup.replace('<svg ', '<svg xmlns="http://www.w3.org/2000/svg" ')
    const objectUrl = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }))
    try {
      const image = new Image()
      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve()
        image.onerror = () => reject(new Error(`Could not render native ${key} icon`))
        image.src = objectUrl
      })
      const canvas = document.createElement('canvas')
      canvas.width = size
      canvas.height = size
      const context = canvas.getContext('2d')
      if (!context) throw new Error('Canvas is unavailable')
      context.drawImage(image, 0, 0, size, size)
      return [key, canvas.toDataURL('image/png').split(',')[1] ?? ''] as const
    } finally {
      URL.revokeObjectURL(objectUrl)
    }
  }))

  return Object.fromEntries(images)
}

function settingsTitle(view: SettingsView, text: (key: UiMessageKey) => string) {
  if (view === 'profile') return text('profile')
  if (view === 'membership') return text('plans')
  if (view === 'language') return text('language')
  if (view === 'contact') return text('contact')
  if (view === 'privacy') return text('privacy')
  if (view === 'terms') return text('terms')
  return text('settings')
}

function MembershipSettings({
  signedIn,
  membership,
  credits,
  pending,
  error,
  onRetry,
  onLogin,
  onUpgrade,
}: {
  signedIn: boolean
  membership: string
  credits: number
  pending: boolean
  error: boolean
  onRetry: () => void
  onLogin: () => void
  onUpgrade: () => void
}) {
  const { text, locale } = useUiText()
  return (
    <div className="video-membership-settings">
      <div className="video-plan-summary">
        <span>{text('currentPlan')}</span>
        <strong>{signedIn ? pending ? '…' : membership : text('notSignedIn')}</strong>
        <p>
          {!signedIn
            ? text('signInIntro')
            : pending
              ? paywallMessages[locale].membershipSyncPending
              : error
                ? paywallMessages[locale].membershipSyncFailed
              : credits > 0
              ? text('creditsAvailable', { count: credits })
              : text('noCredits')}
        </p>
        {signedIn && !pending && !error && (
          <div className="video-progress"><span style={{ width: credits > 0 ? '68%' : '4%' }} /></div>
        )}
      </div>
      {signedIn && error && <button className="video-primary-button" type="button" onClick={onRetry}>{paywallMessages[locale].retry}</button>}
      <p className="video-muted-copy">
        {signedIn
          ? text('membershipInfo')
          : text('signedOutMembershipInfo')}
      </p>
      <button
        className="video-primary-button"
        type="button"
        onClick={signedIn ? onUpgrade : onLogin}
      >
        {signedIn ? text('reviewPlans') : text('login')}
      </button>
    </div>
  )
}

function InfoCopy({ children }: { children: React.ReactNode }) {
  return <div className="video-info-copy">{children}</div>
}

function LegalCopy({ document }: { document: LegalDocument }) {
  const { text } = useUiText()
  return (
    <div className="video-info-copy video-legal-copy">
      <p>{document.lead}</p>
      <small>{text('versionUpdated', { version: document.version, date: document.lastUpdated })}</small>
      {document.sections.map((section) => (
        <section key={section.id}>
          <h3>{section.title}</h3>
          {section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          {section.bullets && <ul>{section.bullets.map((item) => <li key={item}>{item}</li>)}</ul>}
          {section.note && <aside>{section.note}</aside>}
        </section>
      ))}
      <div className="video-legal-references">
        {document.references.map((reference) => <a key={reference.href} href={reference.href} target="_blank" rel="noreferrer">{reference.label}</a>)}
      </div>
    </div>
  )
}
